import React from "react";
import { Pressable, StyleSheet, View, useWindowDimensions } from "react-native";

import { APP_TOKENS as tokens } from "../../../../../styles/tokens";
import { NormalText } from "../../../../common/native";
import ImageFilterIcon, {
  TImageFilterIconName,
} from "../../../../common/web/ImageFilterIcon.web";

const aiSettingOptions = [
  { label: "Blur Tattoos", value: "is_cover_tattoo", icon: "tattoo" },
  {
    label: "Remove Background",
    value: "is_remove_background",
    icon: "remove-background",
  },
  { label: "Hide Faces", value: "is_remove_face", icon: "face" },
  {
    label: "Cover Privates",
    value: "is_cover_private_parts",
    icon: "privacy",
  },
] satisfies Array<{
  label: string;
  value: string;
  icon: TImageFilterIconName;
}>;

type SelectProps = {
  disabled: boolean;
  selectedAiSetting: string[];
  setAiSetting: (values: string[]) => void;
};

const AISetting: React.FC<SelectProps> = ({
  disabled,
  selectedAiSetting,
  setAiSetting,
}) => {
  const { width } = useWindowDimensions();
  const isMobile = width < tokens.spacing.giant * 12;
  const [focusedValue, setFocusedValue] = React.useState<string | null>(null);
  const [hoveredValue, setHoveredValue] = React.useState<string | null>(null);

  const handleSelect = (value: string) => {
    if (disabled) return;
    if (selectedAiSetting.includes(value)) {
      setAiSetting(selectedAiSetting.filter((item) => item !== value));
    } else {
      setAiSetting([...selectedAiSetting, value]);
    }
  };

  const handleCheckboxKeyDown = (
    event: React.KeyboardEvent<HTMLElement>,
    value: string,
  ) => {
    if (event.key !== " " && event.key !== "Spacebar") return;
    event.preventDefault();
    event.stopPropagation();
    handleSelect(value);
  };

  return (
    <View testID="before-after-ai-tools" style={styles.root}>
      <NormalText
        text="AI Tools:"
        style={[styles.heading, isMobile && styles.mobileHeading]}
      />
      <View style={styles.container}>
        {aiSettingOptions.map((item) => {
          const isSelected = selectedAiSetting.includes(item.value);

          return (
            <Pressable
              key={item.value}
              testID={`before-after-ai-tool-${item.icon}`}
              disabled={disabled}
              accessibilityRole="checkbox"
              accessibilityLabel={item.label}
              accessibilityHint="Toggles this AI tool for generated images."
              accessibilityState={{ checked: isSelected, disabled }}
              {...({
                "aria-checked": isSelected,
                "aria-disabled": disabled,
                onKeyDown: (event: React.KeyboardEvent<HTMLElement>) =>
                  handleCheckboxKeyDown(event, item.value),
              } as any)}
              onPress={() => handleSelect(item.value)}
              onFocus={() => setFocusedValue(item.value)}
              onBlur={() => setFocusedValue(null)}
              onHoverIn={() => setHoveredValue(item.value)}
              onHoverOut={() => setHoveredValue(null)}
              style={({ pressed }) => [
                styles.option,
                isMobile ? styles.mobileOption : styles.desktopOption,
                isSelected && styles.selectedOption,
                hoveredValue === item.value &&
                  !disabled &&
                  styles.hoveredOption,
                focusedValue === item.value && styles.focusedOption,
                pressed && !disabled && styles.pressedOption,
                disabled && styles.disabledOption,
              ]}
            >
              <View style={styles.iconContainer}>
                <ImageFilterIcon
                  name={item.icon}
                  color={tokens.color.textPrimary}
                  size={isMobile ? tokens.fontSize.xxl : tokens.fontSize.xxxl}
                />
              </View>
              <NormalText
                text={item.label}
                style={[
                  styles.optionText,
                  isMobile ? styles.mobileOptionText : styles.desktopOptionText,
                ]}
              />
            </Pressable>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    width: "100%",
    gap: tokens.spacing.md,
    alignItems: "center",
  },
  heading: {
    fontSize: tokens.fontSize.lg,
  },
  mobileHeading: {
    fontSize: tokens.fontSize.sm,
  },
  container: {
    width: "100%",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: tokens.spacing.sm,
  },
  option: {
    borderRadius: tokens.radius.md,
    borderWidth: tokens.borderWidth.thin,
    borderColor: tokens.color.borderStrong,
    padding: tokens.spacing.sm,
    justifyContent: "flex-start",
    alignItems: "center",
  },
  mobileOption: {
    width: tokens.spacing.giant + tokens.spacing.sm,
    minHeight: tokens.spacing.giant + tokens.spacing.xxl,
  },
  desktopOption: {
    width: tokens.spacing.massive * 2,
    minHeight: tokens.spacing.massive * 2,
  },
  selectedOption: {
    backgroundColor: tokens.color.surfaceAccent,
    borderColor: tokens.color.accentDark,
  },
  hoveredOption: {
    backgroundColor: tokens.color.surfaceAccent,
  },
  focusedOption: {
    outlineColor: tokens.color.accentDark,
    outlineOffset: tokens.spacing.xs,
    outlineStyle: "solid",
    outlineWidth: tokens.borderWidth.strong,
  } as any,
  pressedOption: {
    opacity: tokens.opacity.pressed,
  },
  disabledOption: {
    opacity: tokens.opacity.disabled,
  },
  optionText: {
    textAlign: "center",
    marginTop: tokens.spacing.xs,
  },
  mobileOptionText: {
    fontSize: tokens.fontSize.xs,
    lineHeight: tokens.lineHeight.xs,
  },
  desktopOptionText: {
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
  },
  iconContainer: {
    minHeight: tokens.spacing.xxxl,
    justifyContent: "center",
    alignItems: "center",
  },
});

export default AISetting;
